import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Grid,
  useMediaQuery,
  Alert,
  Snackbar,
  Button,
} from "@mui/material";
import SubTitle from "../common/SubTitle";
import MapSection from "./MapSection";
import BoundarySection from "./BoundarySection";
import VehiclesSection from "./VehiclesSection";
import SiteDetails from "./SiteDetails";
import axios from "axios";
import config from "../../config.js";
import { Skeleton } from "@mui/material";

async function getSiteById(id) {

  try {
    const response = await axios.get(`${config.baseURL}/site/id?id=${id}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.log("Error in fetching site data: ", error);
    throw error;
  }
}

async function createSite(site) {
  try {
    const response = await axios.post(`${config.baseURL}/site`, site, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.log("Error in creating site: ", error);
    throw error;
  }
}

async function updateSite(site) {
  try {
    const response = await axios.put(`${config.baseURL}/site`, site, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.log("Error in updating site: ", error);
    throw error;
  }
}

async function assignVehiclesToSite(id, vehicleIds) {
  try {
    const response = await axios.put(
      `${config.baseURL}/site/vehicles`,
      { siteId: id, vehicleIds },
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.log("Error in assigning vehicles to site: ", error);
    throw error;
  }
}

async function getAllTheVehicles() {
  try {
    const response = await axios.get(`${config.baseURL}/vehicle/organization`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.log("Error in fetching vehicles: ", error);
    throw error;
  }
}

async function getUsersForTheOrg(organizationId) {
  try {

    const response = await axios.get(
      `${config.baseURL}/user/organization/${organizationId}`,
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.log("Error in fetching user data: ", error);
    throw error;
  }
}

export default function AddSiteMain() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const userStateString = localStorage.getItem("userState");
  const mapRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:600px)");
  const userState = JSON.parse(userStateString);
  const organizationId = userState?.user?.organizationId || "";

  const [site, setSite] = useState({
    siteId: "",
    name: "",
    description: "",
    siteIncharge: "",
    organizationId: organizationId,
    boundaryType: "CIRCULAR",
    sitCoordinates: [{ longitude: 0, latitude: 0 }],
    radius: "",
  });

  const [errors, setErrors] = useState({
    siteId: "",
    name: "",
    radius: "",
    siteIncharge: "",
    coordinates: [],
    general: "",
  });

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [boundaryType, setBoundaryType] = useState("circular");
  const [mapCenter, setMapCenter] = useState({ lat: 13.025, lng: 77.628 });
  const [mapLoaded, setMapLoaded] = useState(false);
  const [touched, setTouched] = useState({});
  const [mapZoom, setMapZoom] = useState(15);

  const [initialSiteName, setInitialSiteName] = useState("Site");

  const getNormalizedCoordinate = (coordinate = {}) => ({
    ...coordinate,
    longitude: coordinate?.longitude ?? "",
    latitude: coordinate?.latitude ?? "",
  });

  const getApiBoundaryType = (value) => {
    if (typeof value !== "string" || !value.trim()) {
      return "CIRCULAR";
    }

    const normalizedValue = value.trim().toUpperCase();
    return normalizedValue === "CUSTOM" ? "CUSTOM" : "CIRCULAR";
  };

  const getFallbackCoordinates = (coordinates = []) => {
    if (Array.isArray(coordinates) && coordinates.length > 0) {
      return coordinates.map((coordinate) => getNormalizedCoordinate(coordinate));
    }

    return [getNormalizedCoordinate()];
  };

  const resolveBoundaryType = (siteData) => {
    const backendBoundaryType =
      siteData?.boundaryType || siteData?.siteType || null;
    const normalizedBoundaryType =
      typeof backendBoundaryType === "string"
        ? backendBoundaryType.toLowerCase()
        : null;

    if (
      normalizedBoundaryType === "circular" ||
      normalizedBoundaryType === "custom"
    ) {
      return normalizedBoundaryType;
    }

    return "circular";
  };

  const normalizeSiteForBoundaryType = (siteData, nextBoundaryType) => {
    const normalizedCoordinates = getFallbackCoordinates(siteData?.sitCoordinates);
    const apiBoundaryType = getApiBoundaryType(nextBoundaryType);

    if (nextBoundaryType === "circular") {
      return {
        ...siteData,
        boundaryType: apiBoundaryType,
        radius: siteData?.radius ?? "",
        sitCoordinates: [normalizedCoordinates[0]],
      };
    }

    return {
      ...siteData,
      boundaryType: apiBoundaryType,
      radius: "",
      sitCoordinates: normalizedCoordinates,
    };
  };

  const buildSitePayload = (currentSite, currentBoundaryType, isEditing) => {
    const normalizedSite = normalizeSiteForBoundaryType(
      currentSite,
      currentBoundaryType
    );
    const boundaryType = getApiBoundaryType(currentBoundaryType);
    const cleanedCoordinates = normalizedSite.sitCoordinates
      .filter((coord) => coord.longitude !== "" && coord.latitude !== "")
      .map((coord) => ({
        latitude: Number(coord.latitude),
        longitude: Number(coord.longitude),
      }))
      .filter(
        (coord) =>
          !Number.isNaN(coord.latitude) && !Number.isNaN(coord.longitude)
      );

    const payload = {
      siteId: normalizedSite.siteId,
      name: normalizedSite.name,
      description: normalizedSite.description || "",
      organizationId: Number(normalizedSite.organizationId),
      siteIncharge: normalizedSite.siteIncharge || null,
      boundaryType,
      radius:
        boundaryType === "CIRCULAR" && normalizedSite.radius !== ""
          ? parseFloat(normalizedSite.radius)
          : null,
      sitCoordinates:
        boundaryType === "CIRCULAR"
          ? cleanedCoordinates.slice(0, 1)
          : cleanedCoordinates,
    };

    if (isEditing && currentSite?.id) {
      payload.id = currentSite.id;
    }

    return payload;
  };

  const handleBoundaryTypeChange = (nextBoundaryType) => {
    setBoundaryType(nextBoundaryType);
    setSite((prevSite) => normalizeSiteForBoundaryType(prevSite, nextBoundaryType));
    setErrors((prev) => ({
      ...prev,
      radius: "",
      general: "",
      coordinates: [],
    }));
  };

  const calculateZoomLevel = (radiusInKm) => {
    if (!radiusInKm) return 15;
    const radius = parseFloat(radiusInKm);
    if (radius <= 0.1) return 18;
    if (radius <= 0.25) return 17;
    if (radius <= 0.5) return 16;
    if (radius <= 1) return 15;
    if (radius <= 2) return 14;
    if (radius <= 5) return 13;
    if (radius <= 10) return 12;
    if (radius <= 20) return 11;
    if (radius <= 40) return 10;
    if (radius <= 80) return 9;
    if (radius <= 100) return 8;
    return 7;
  };

  const calculatePolygonZoomAndCenter = (coordinates) => {
    if (!coordinates || coordinates.length === 0) {
      return { center: { lat: 13.025, lng: 77.628 }, zoom: 14 };
    }

    const lats = coordinates
      .map((coord) => parseFloat(coord.latitude))
      .filter((lat) => !isNaN(lat));
    const lngs = coordinates
      .map((coord) => parseFloat(coord.longitude))
      .filter((lng) => !isNaN(lng));

    if (lats.length === 0 || lngs.length === 0) {
      return { center: { lat: 13.025, lng: 77.628 }, zoom: 10 };
    }

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const center = {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    };

    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    let zoom;
    if (maxDiff < 0.01) zoom = 15;
    else if (maxDiff < 0.05) zoom = 13;
    else if (maxDiff < 0.1) zoom = 12;
    else if (maxDiff < 0.5) zoom = 10;
    else if (maxDiff < 1) zoom = 9;
    else if (maxDiff < 5) zoom = 8;
    else zoom = 7;

    return { center, zoom };
  };

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        if (siteId) {
          setIsUpdating(true);
          const siteData = await getSiteById(siteId);
          const resolvedBoundaryType = resolveBoundaryType(siteData);
          const normalizedSiteData = normalizeSiteForBoundaryType(
            {
            ...siteData,
            siteIncharge: siteData.siteIncharge || "",
            },
            resolvedBoundaryType
          );

          setSite(normalizedSiteData);
          setInitialSiteName(siteData.name || "Site");

          if (
            normalizedSiteData.sitCoordinates &&
            normalizedSiteData.sitCoordinates.length > 0
          ) {
            if (resolvedBoundaryType === "circular" && normalizedSiteData.radius) {
              setMapCenter({
                lat: parseFloat(normalizedSiteData.sitCoordinates[0].latitude) || 0,
                lng: parseFloat(normalizedSiteData.sitCoordinates[0].longitude) || 0,
              });
              setMapZoom(calculateZoomLevel(normalizedSiteData.radius));
            } else {
              const { center, zoom } = calculatePolygonZoomAndCenter(
                normalizedSiteData.sitCoordinates
              );
              setMapCenter(center);
              setMapZoom(zoom);
            }
          }
          setBoundaryType(resolvedBoundaryType);
          if (siteData.vehicles && siteData.vehicles.length > 0) {
            setSelectedVehicles(siteData.vehicles.map((v) => v.id));
          }
        }
        let vehicleData;

        vehicleData = await getAllTheVehicles();

        setVehicles(vehicleData);

        if (organizationId) {
          const userData = await getUsersForTheOrg(organizationId);
          console.log("userid anveed checck", userData);
          setUsers(userData.users);
        }
      } catch (err) {
        setNotification({
          open: true,
          message: `Failed to load data: ${
            err.response?.data?.message || err.message
          }`,
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [siteId, organizationId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "radius" && value) {
      const numValue = parseFloat(value);
      if (numValue > 100) {
        newValue = "100";
      }
    }

    setSite((prevSite) => ({
      ...prevSite,
      [name]: newValue,
    }));

    if (name === "radius" && newValue) {
      setMapZoom(calculateZoomLevel(newValue));
    }

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCoordinatesChange = (index, key, value) => {
    const newCoordinates = [...site.sitCoordinates];
    if (index >= newCoordinates.length) {
      // Add a new coordinate if the index doesn't exist
      newCoordinates.push({ longitude: "", latitude: "" });
    }
    newCoordinates[index][key] = value;

    const newTouched = { ...touched };
    newTouched[`coordinate_${index}_${key}`] = true;
    setTouched(newTouched);

    if (errors.coordinates && errors.coordinates[index]) {
      const newCoordErrors = [...errors.coordinates];
      newCoordErrors[index] = "";
      setErrors((prev) => ({
        ...prev,
        coordinates: newCoordErrors,
      }));
    }

    if (index === 0 && boundaryType === "circular") {
      setMapCenter({
        lat:
          key === "latitude"
            ? parseFloat(value) || mapCenter.lat
            : parseFloat(newCoordinates[0].latitude) || mapCenter.lat,
        lng:
          key === "longitude"
            ? parseFloat(value) || mapCenter.lng
            : parseFloat(newCoordinates[0].longitude) || mapCenter.lng,
      });
    } else if (boundaryType === "custom") {
      // Update map center based on the average of all coordinates
      const validCoords = newCoordinates.filter(
        (coord) => coord.latitude && coord.longitude
      );
      if (validCoords.length > 0) {
        const { center } = calculatePolygonZoomAndCenter(validCoords);
        setMapCenter(center);
      }
    }

    setSite((prevSite) => ({
      ...prevSite,
      sitCoordinates: newCoordinates,
    }));
  };

  const handleMapClick = (e) => {
    if (boundaryType === "circular") {
      const newCoordinates = [
        {
          longitude: e.latLng.lng().toFixed(6),
          latitude: e.latLng.lat().toFixed(6),
        },
      ];
      setSite((prev) => ({
        ...prev,
        sitCoordinates: newCoordinates,
      }));
      setMapCenter({
        lat: parseFloat(e.latLng.lat()),
        lng: parseFloat(e.latLng.lng()),
      });
    } else {
      // For custom boundary, check if last coordinate is empty
      const lastCoord = site.sitCoordinates[site.sitCoordinates.length - 1];
      if (!lastCoord || (lastCoord.longitude && lastCoord.latitude)) {
        const newCoordinate = {
          longitude: e.latLng.lng().toFixed(6),
          latitude: e.latLng.lat().toFixed(6),
        };
        setSite((prev) => ({
          ...prev,
          sitCoordinates: [...prev.sitCoordinates, newCoordinate],
        }));
        setErrors((prev) => ({
          ...prev,
          coordinates: [...(prev.coordinates || []), ""],
        }));
      } else {
        // Update the last empty coordinate
        handleCoordinatesChange(
          site.sitCoordinates.length - 1,
          "longitude",
          e.latLng.lng().toFixed(6)
        );
        handleCoordinatesChange(
          site.sitCoordinates.length - 1,
          "latitude",
          e.latLng.lat().toFixed(6)
        );
      }
    }
  };

  const handleMarkerDrag = (index, e) => {
    const newCoordinates = [...site.sitCoordinates];
    newCoordinates[index] = {
      longitude: e.latLng.lng().toFixed(6),
      latitude: e.latLng.lat().toFixed(6),
    };

    if (index === 0 && boundaryType === "circular") {
      setMapCenter({
        lat: parseFloat(e.latLng.lat()),
        lng: parseFloat(e.latLng.lng()),
      });
    }

    setSite((prev) => ({
      ...prev,
      sitCoordinates: newCoordinates,
    }));
  };

  const handlePlaceSelected = (place) => {
    if (!place.geometry || !place.geometry.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    if (boundaryType === "circular") {
      const newCoordinates = [
        {
          longitude: lng.toFixed(6),
          latitude: lat.toFixed(6),
        },
      ];
      setSite((prev) => ({
        ...prev,
        sitCoordinates: newCoordinates,
      }));
    } else {
      const newCoordinate = {
        longitude: lng.toFixed(6),
        latitude: lat.toFixed(6),
      };
      setSite((prev) => ({
        ...prev,
        sitCoordinates: [...prev.sitCoordinates, newCoordinate],
      }));
      setErrors((prev) => ({
        ...prev,
        coordinates: [...(prev.coordinates || []), ""],
      }));
    }

    setMapCenter({ lat, lng });
    setMapZoom(18);
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
    validateField(field);
  };

  const handleCoordinateBlur = (index, key) => {
    const fieldName = `coordinate_${index}_${key}`;
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
    validateCoordinate(index, key);
  };

  const validateField = (field) => {
    let newErrors = { ...errors };

    switch (field) {
      case "siteId":
        if (!site.siteId.trim()) {
          newErrors.siteId = "Site ID is required";
        } else if (!/^[a-zA-Z0-9_-]+$/.test(site.siteId)) {
          newErrors.siteId =
            "Site ID should only contain letters, numbers, hyphens, and underscores";
        } else {
          newErrors.siteId = "";
        }
        break;

      case "name":
        if (!site.name.trim()) {
          newErrors.name = "Site name is required";
        } else {
          newErrors.name = "";
        }
        break;

      case "radius":
        if (boundaryType === "circular") {
          if (!site.radius) {
            newErrors.radius = "Radius is required for circular boundary";
          } else if (parseFloat(site.radius) <= 0) {
            newErrors.radius = "Radius must be greater than 0";
          } else if (parseFloat(site.radius) > 100) {
            newErrors.radius = "Radius cannot exceed 100";
          } else {
            newErrors.radius = "";
          }
        } else {
          newErrors.radius = "";
        }
        break;

      case "siteIncharge":
        // Optional field, no validation needed unless specific rules apply
        newErrors.siteIncharge = "";
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return !newErrors[field];
  };

  const validateCoordinate = (index, key) => {
    const coordinate = site.sitCoordinates[index];
    const value = coordinate[key];
    let newCoordErrors = [
      ...(errors.coordinates || Array(site.sitCoordinates.length).fill("")),
    ];

    if (key === "longitude") {
      if (value === "" || value === null) {
        newCoordErrors[index] = "Longitude is required";
      } else if (
        isNaN(value) ||
        parseFloat(value) < -180 ||
        parseFloat(value) > 180
      ) {
        newCoordErrors[index] = "Longitude must be between -180 and 180";
      } else {
        newCoordErrors[index] = "";
      }
    }

    if (key === "latitude") {
      if (value === "" || value === null) {
        newCoordErrors[index] = "Latitude is required";
      } else if (
        isNaN(value) ||
        parseFloat(value) < -90 ||
        parseFloat(value) > 90
      ) {
        newCoordErrors[index] = "Latitude must be between -90 and 90";
      } else {
        newCoordErrors[index] = "";
      }
    }

    setErrors((prev) => ({
      ...prev,
      coordinates: newCoordErrors,
    }));

    return !newCoordErrors[index];
  };

  const validatePolygon = () => {
    if (boundaryType !== "custom" || site.sitCoordinates.length < 3) {
      return true;
    }

    const uniquePoints = new Set();
    let isValid = true;

    site.sitCoordinates.forEach((coord, index) => {
      const pointKey = `${coord.longitude},${coord.latitude}`;
      if (uniquePoints.has(pointKey)) {
        const newCoordErrors = [
          ...(errors.coordinates || Array(site.sitCoordinates.length).fill("")),
        ];
        newCoordErrors[index] = "Duplicate coordinate point";
        setErrors((prev) => ({
          ...prev,
          coordinates: newCoordErrors,
        }));
        isValid = false;
      }
      uniquePoints.add(pointKey);
    });

    return isValid;
  };

  const validateForm = () => {
    let isFormValid = true;
    let newErrors = { ...errors };

    if (!site.siteId.trim()) {
      newErrors.siteId = "Site ID is required";
      isFormValid = false;
    } else if (!/^[a-zA-Z0-9_-]+$/.test(site.siteId)) {
      newErrors.siteId =
        "Site ID should only contain letters, numbers, hyphens, and underscores";
      isFormValid = false;
    } else {
      newErrors.siteId = "";
    }

    if (!site.name.trim()) {
      newErrors.name = "Site name is required";
      isFormValid = false;
    } else {
      newErrors.name = "";
    }

    newErrors.siteIncharge = ""; // Optional field

    const newCoordErrors = Array(site.sitCoordinates.length).fill("");

    site.sitCoordinates.forEach((coord, index) => {
      if (coord.longitude === "" || coord.longitude === null) {
        newCoordErrors[index] = "Longitude is required";
        isFormValid = false;
      } else if (
        isNaN(coord.longitude) ||
        parseFloat(coord.longitude) < -180 ||
        parseFloat(coord.longitude) > 180
      ) {
        newCoordErrors[index] = "Longitude must be between -180 and 180";
        isFormValid = false;
      }

      if (coord.latitude === "" || coord.latitude === null) {
        newCoordErrors[index] = newCoordErrors[index]
          ? `${newCoordErrors[index]} and Latitude is required`
          : "Latitude is required";
        isFormValid = false;
      } else if (
        isNaN(coord.latitude) ||
        parseFloat(coord.latitude) < -90 ||
        parseFloat(coord.latitude) > 90
      ) {
        newCoordErrors[index] = newCoordErrors[index]
          ? `${newCoordErrors[index]} and Latitude must be between -90 and 90`
          : "Latitude must be between -90 and 90";
        isFormValid = false;
      }
    });

    newErrors.coordinates = newCoordErrors;

    if (boundaryType === "circular") {
      if (!site.radius) {
        newErrors.radius = "Radius is required for circular boundary";
        isFormValid = false;
      } else if (parseFloat(site.radius) <= 0) {
        newErrors.radius = "Radius must be greater than 0";
        isFormValid = false;
      } else if (parseFloat(site.radius) > 100) {
        newErrors.radius = "Radius cannot exceed 100";
        isFormValid = false;
      } else {
        newErrors.radius = "";
      }
    } else {
      newErrors.radius = "";
    }

    if (boundaryType === "custom" && site.sitCoordinates.length < 3) {
      newErrors.general = "Custom boundary requires at least 3 coordinates";
      isFormValid = false;
    } else if (boundaryType === "custom") {
      isFormValid = validatePolygon() && isFormValid;
    }

    setErrors(newErrors);
    return isFormValid;
  };

  const addCoordinate = () => {
    // Only add new coordinate if last coordinate has values
    const lastCoord = site.sitCoordinates[site.sitCoordinates.length - 1];
    if (
      site.sitCoordinates.length === 0 ||
      (lastCoord && lastCoord.longitude && lastCoord.latitude)
    ) {
      setSite((prevSite) => ({
        ...prevSite,
        sitCoordinates: [
          ...prevSite.sitCoordinates,
          { longitude: "", latitude: "" },
        ],
      }));
      setErrors((prev) => ({
        ...prev,
        coordinates: [...(prev.coordinates || []), ""],
      }));
    }
  };

  const removeCoordinate = (index) => {
    if (site.sitCoordinates.length > 1) {
      const newCoordinates = [...site.sitCoordinates];
      newCoordinates.splice(index, 1);
      setSite((prevSite) => ({
        ...prevSite,
        sitCoordinates: newCoordinates,
      }));
      const newCoordErrors = [...(errors.coordinates || [])];
      newCoordErrors.splice(index, 1);
      setErrors((prev) => ({
        ...prev,
        coordinates: newCoordErrors,
      }));
    }
  };

  const handleVehicleSelection = (event) => {
    setSelectedVehicles(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const allTouched = {
      siteId: true,
      name: true,
      siteIncharge: true,
      radius: boundaryType === "circular",
    };

    site.sitCoordinates.forEach((_, index) => {
      allTouched[`coordinate_${index}_longitude`] = true;
      allTouched[`coordinate_${index}_latitude`] = true;
    });

    setTouched(allTouched);

    if (!validateForm()) {
      setNotification({
        open: true,
        message: "Please correct the errors before submitting",
        severity: "error",
      });
      return;
    }

    setIsLoading(true);
    const siteData = buildSitePayload(site, boundaryType, isUpdating);

    const action = isUpdating ? updateSite : createSite;
    action(siteData)
      .then((savedSite) => {
        const resolvedSiteId =
          savedSite?.id || site?.id || (siteId ? Number(siteId) : null);

        if (selectedVehicles.length > 0 && resolvedSiteId) {
          return assignVehiclesToSite(resolvedSiteId, selectedVehicles).then(
            () => savedSite
          );
        }

        return savedSite;
      })
      .then(() => {
        setNotification({
          open: true,
          message: `Site ${isUpdating ? "updated" : "created"} successfully`,
          severity: "success",
        });
        navigate("/site", { replace: true });
      })
      .catch((err) => {
        const errorMessage = err.response?.data?.message || err.message;
        if (
          err.response?.data?.code === "P2002" &&
          errorMessage.includes("siteIncharge")
        ) {
          setErrors((prev) => ({
            ...prev,
            siteIncharge: "Site Incharge is already assigned to another site",
          }));
          setNotification({
            open: true,
            message: "Site Incharge is already assigned to another site",
            severity: "error",
          });
        } else {
          setNotification({
            open: true,
            message: `Failed to ${
              isUpdating ? "update" : "create"
            } site: ${errorMessage}`,
            severity: "error",
          });
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleCancel = () => {
    navigate("/site");
  };

  const createVehicle = () => {
    navigate("/vehicle/add");
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
    setMapLoaded(true);
  };

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  return (
    <>
      <SubTitle
        title={isUpdating ? "Update Site" : "Create Site"}
        path={
          isUpdating
            ? `${orgName || "organization"} > Sites > ${initialSiteName} `
            : `${orgName || "organization"} > Sites > Create`
        }
      />
      {/* <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar> */}

      {isLoading ? (
        <Box
          sx={{ margin: { xs: "0px 16px", sm: "0px 24px", md: "0px 40px" } }}
        >
          <Box
            sx={{
              minHeight: { xs: "auto", md: "556px" },
              borderRadius: "10px",
              padding: { xs: "16px", sm: "20px", md: "24px" },
              background: "#FFFFFF",
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 1px 5px 0px #00000040",
              mb: 3,
              overflow: "hidden",
            }}
          >
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rectangular" height={350} sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={350} sx={{ mb: 2 }} />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              borderRadius: "10px",
              padding: { xs: "16px", sm: "20px", md: "24px 24px 32px 24px" },
              background: "#FFFFFF",
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 2px 5px 0px #00000040",
              overflow: "hidden",
            }}
          >
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
              </Grid>
            </Grid>
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}
            >
              <Skeleton variant="rectangular" width={122} height={55} />
              <Skeleton variant="rectangular" width={125} height={55} />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            margin: { xs: "0px 16px", sm: "0px 24px", md: "0px 40px" },
          }}
        >
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.general}
            </Alert>
          )}

          <Box
            sx={{
              minHeight: { xs: "auto", md: "556px" },
              borderRadius: "10px",
              padding: { xs: "16px", sm: "20px", md: "24px" },
              gap: "20px",
              background: "#FFFFFF",
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 1px 5px 0px #00000040",
              mb: 3,
              overflow: "hidden",
            }}
          >
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} md={8}>
                <MapSection
                  boundaryType={boundaryType}
                  site={site}
                  mapCenter={mapCenter}
                  mapZoom={mapZoom}
                  mapLoaded={mapLoaded}
                  onMapLoad={onMapLoad}
                  onMapClick={handleMapClick}
                  onMarkerDrag={handleMarkerDrag}
                  onPlaceSelected={handlePlaceSelected}
                  isMobile={isMobile}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <BoundarySection
                  boundaryType={boundaryType}
                  handleBoundaryTypeChange={handleBoundaryTypeChange}
                  site={site}
                  handleCoordinatesChange={handleCoordinatesChange}
                  handleInputChange={handleInputChange}
                  addCoordinate={addCoordinate}
                  removeCoordinate={removeCoordinate}
                  errors={errors}
                  touched={touched}
                  handleCoordinateBlur={handleCoordinateBlur}
                  handleBlur={handleBlur}
                  isMobile={isMobile}
                  setErrors={setErrors}
                  setTouched={setTouched}
                />
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              borderRadius: "10px",
              padding: { xs: "16px", sm: "20px", md: "24px 24px 32px 24px" },
              gap: { xs: "20px", md: "36px" },
              background: "#FFFFFF",
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 2px 5px 0px #00000040",
              overflow: "hidden",
            }}
          >
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} md={8}>
                <SiteDetails
                  site={site}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  isUpdating={isUpdating}
                  users={users}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <VehiclesSection
                  vehicles={vehicles}
                  selectedVehicles={selectedVehicles}
                  handleVehicleSelection={handleVehicleSelection}
                  createVehicle={createVehicle}
                  isMobile={isMobile}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 3,
                flexDirection: { xs: isMobile ? "column" : "row" },
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancel}
                disabled={isLoading}
                sx={{
                  width: "122px",
                  height: "55px",
                  borderRadius: "8px",
                  borderWidth: "1px",
                  padding: "16px 24px",
                  background: "#F2F2F3",
                  border: "1px solid #414958",
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "100%",
                  textTransform: "none",
                  letterSpacing: "0%",
                  color: "#111214",
                  "&:hover": { bgcolor: "#E0E0E0", borderColor: "#D0D0D0" },
                  width: { xs: isMobile ? "100%" : "122px" },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  width: "125px",
                  height: "55px",
                  borderRadius: "8px",
                  borderWidth: "1px",
                  padding: "16px 24px",
                  background: "#F9E719",
                  border: "1px solid #414958",
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "100%",
                  textTransform: "none",
                  color: "#111214",
                  "&:hover": { bgcolor: "#E0D618" },
                  width: { xs: isMobile ? "100%" : "125px" },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : isUpdating ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
